import { useEffect, useState } from "react";
import { Play, X, ExternalLink, Check, ArrowRight, Sparkles, Calendar } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Extract a YouTube video ID from any common YouTube URL shape.
function youtubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/watch\?.*[?&]v=)([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

// Normalise product.demo_video_url into a { kind, url, id, thumbnail } object.
// Supports:
//   - YouTube (any URL shape) → iframe embed
//   - Absolute HTTP(S) mp4 → <video>
//   - /api/media/... → self-hosted mp4 through our range-streaming endpoint
function resolveMedia(rawUrl) {
  if (!rawUrl) return null;
  const yt = youtubeId(rawUrl);
  if (yt) {
    return {
      kind: "youtube",
      id: yt,
      // Embed URL — autoplay + minimal chrome; muted needed for autoplay policy.
      embedUrl: `https://www.youtube-nocookie.com/embed/${yt}?rel=0&modestbranding=1&playsinline=1`,
      previewUrl: `https://www.youtube-nocookie.com/embed/${yt}?autoplay=1&mute=1&loop=1&playlist=${yt}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1`,
      thumbnail: `https://img.youtube.com/vi/${yt}/maxresdefault.jpg`,
    };
  }
  const absolute = /^https?:\/\//i.test(rawUrl)
    ? rawUrl
    : rawUrl.startsWith("/api/") ? `${BACKEND_URL}${rawUrl}` : rawUrl;
  return { kind: "video", url: absolute };
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    document.title = "Products — ZeroLabs Digitals";
    api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);

  return (
    <div className="pt-24 pb-24" data-testid="page-products">
      <section className="relative overflow-hidden">
        <div className="orb orb-purple" style={{ top: "-100px", right: "-60px", width: "360px", height: "360px" }} />
        <div className="orb orb-orange" style={{ top: "120px", left: "-80px", width: "280px", height: "280px", opacity: 0.45 }} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="eyebrow inline-flex items-center gap-2">
            <Sparkles size={12}/> Our Products
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">
            Software we built, <span className="text-gradient">ready to use</span>.
          </h1>
          <p className="mt-4 text-[#B6B0C7] max-w-2xl leading-relaxed">
            Live products and what's coming next from ZeroLabs Digitals. Each product is built on the
            same engineering foundation that powers our client engagements — secure, scalable, and
            designed for real-world operations.
          </p>
          <p className="mt-3 text-[#8C859E] text-sm">
            Ask Zero <span className="text-[#3B82F6]">"what products do you offer?"</span> via the chat or voice agent — you'll get an instant tour with links.
          </p>
        </div>
      </section>

      <section className="mt-12 max-w-7xl mx-auto px-6 md:px-10">
        {products.length === 0 ? (
          <div className="card p-10 text-center text-[#B6B0C7]">Loading products…</div>
        ) : (
          <div className="space-y-16">
            {products.map((p, idx) => {
              const reverse = idx % 2 === 1;
              const live = !!(p.url && p.url !== "#");
              const host = p.url && p.url !== "#" ? new URL(p.url).hostname : "coming-soon.zerolabs.digital";
              const media = resolveMedia(p.demo_video_url);
              return (
                <div key={p.id} className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`product-${p.id}`}>
                  <div className="lg:col-span-6 relative">
                    <div className="absolute -inset-4 bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.18),transparent_60%)] blur-2xl"/>
                    <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
                      {media?.kind === "video" ? (
                        <video src={media.url} autoPlay muted loop playsInline className="w-full h-[360px] object-cover" data-testid={`product-${p.id}-video-preview`}/>
                      ) : media?.kind === "youtube" ? (
                        <iframe
                          src={media.previewUrl}
                          title={`${p.name} demo preview`}
                          data-testid={`product-${p.id}-youtube-preview`}
                          className="w-full h-[360px] pointer-events-none"
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          loading="lazy"
                        />
                      ) : (
                        <img src={p.image} alt={p.name} className="w-full h-[360px] object-cover"/>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#08060F] via-transparent to-transparent pointer-events-none"/>
                      {media && (
                        <button data-testid={`product-${p.id}-play-overlay`} onClick={() => setActiveVideo({ name: p.name, media })}
                          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors" aria-label={`Play ${p.name} demo`}>
                          <span className="w-20 h-20 rounded-full bg-[#3B82F6] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                            <Play size={32} fill="currentColor"/>
                          </span>
                        </button>
                      )}
                      <div className="absolute top-4 left-4 pointer-events-none">
                        <span className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] border ${live ? "bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/40" : "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/40"}`}>{p.tag}</span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                        <span className="px-2 py-1 rounded-md bg-[#08060F]/80 border border-white/10 text-xs font-mono text-[#22D3EE]">{host}</span>
                        {live ? (
                          <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-white inline-flex items-center gap-1 hover:text-[#22D3EE] pointer-events-auto">Visit <ExternalLink size={12}/></a>
                        ) : (
                          <span className="text-xs text-[#B6B0C7]">Demo video coming soon</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-6">
                    <div className="eyebrow">{p.tagline}</div>
                    <h3 className="mt-2 text-3xl md:text-4xl font-bold text-white">{p.name}</h3>
                    <p className="mt-4 text-[#B6B0C7] leading-relaxed">{p.description}</p>
                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3">
                      {p.highlights.map((h) => (
                        <li key={h} className="flex items-center gap-2 text-sm text-[#D6D2E1]">
                          <Check size={14} className="text-[#3B82F6]"/> {h}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-7 flex flex-wrap gap-3">
                      {p.landing_url ? (
                        <a href={p.landing_url} target="_blank" rel="noreferrer" className="btn-primary" data-testid={`product-${p.id}-more-info`}>
                          More Info <ExternalLink size={14}/>
                        </a>
                      ) : live ? (
                        <a href={p.url} target="_blank" rel="noreferrer" className="btn-primary" data-testid={`product-${p.id}-launch`}>
                          More Info <ExternalLink size={14}/>
                        </a>
                      ) : (
                        <button disabled className="btn-primary opacity-60 cursor-not-allowed" data-testid={`product-${p.id}-launch`}>
                          Notify me at launch
                        </button>
                      )}
                      {media ? (
                        <button onClick={() => setActiveVideo({ name: p.name, media })} className="btn-secondary" data-testid={`product-${p.id}-watch-demo`}>
                          <Play size={14}/> Watch demo
                        </button>
                      ) : (
                        <a href="/contact#book" className="btn-secondary">Request a demo</a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-20 max-w-5xl mx-auto px-6 md:px-10 text-center">
        <div className="card p-10 glow-border">
          <div className="eyebrow">Building something custom?</div>
          <h3 className="mt-3 text-2xl md:text-3xl font-bold text-white">Need a product like these — built for your business?</h3>
          <p className="mt-3 text-[#B6B0C7] max-w-xl mx-auto">
            Our products started as client engagements. If you have a similar idea or workflow, we'd love to scope it with you.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/#book" className="btn-primary"><Calendar size={16}/> Book a consultation</a>
            <a href="/contact" className="btn-secondary">Send a brief <ArrowRight size={14}/></a>
          </div>
        </div>
      </section>

      {activeVideo && (
        <div data-testid="video-lightbox" role="dialog" aria-modal="true" onClick={() => setActiveVideo(null)}
          className="fixed inset-0 z-[70] flex items-center justify-center p-6"
          style={{ background: "rgba(8, 6, 15, 0.88)", backdropFilter: "blur(8px)" }}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl">
            <div className="flex items-center justify-between mb-3">
              <div className="text-white font-semibold">{activeVideo.name} — demo</div>
              <button data-testid="video-lightbox-close" onClick={() => setActiveVideo(null)} className="text-[#B6B0C7] hover:text-white p-1"><X/></button>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
              {activeVideo.media?.kind === "youtube" ? (
                <iframe
                  src={`${activeVideo.media.embedUrl}&autoplay=1`}
                  title={`${activeVideo.name} demo`}
                  data-testid="video-lightbox-iframe"
                  className="w-full aspect-video block"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              ) : (
                <video src={activeVideo.media.url} controls autoPlay playsInline className="w-full h-auto max-h-[78vh] block" data-testid="video-lightbox-player"/>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
